import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Modal, Button, Select, MenuItem, CircularProgress } from "@mui/material";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import { useAddConditionMutation, useGetCategoriesQuery, useUpdateConditionMutation } from "../service/features/api";
import { VEHICLE_TYPE } from "../constants/enum";
import { getVehicleTypeLabel } from "../utils/vehicle";
import { toast } from "react-toastify";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  conditionData?: any;
}

export default function AddCondition({
  open,
  onClose,
  setOpen,
  conditionData
}: ModalProps) {

  const [name, setName] = useState<string>("");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedVehicleType, setSelectedVehicleType] = useState<number | "">("");
  const [errors, setErrors] = useState<any>({});

  const { data: categoriesData } = useGetCategoriesQuery({ page: 1, limit: 100 });
  const [addCondition, { isLoading: isAdding }] = useAddConditionMutation();
  const [updateCondition, { isLoading: isUpdating }] = useUpdateConditionMutation();

  useEffect(() => {
    if (conditionData && open) {
      setName(conditionData.name || "");

      let catIds: string[] = [];
      if (conditionData.categories && Array.isArray(conditionData.categories)) {
        catIds = conditionData.categories.map((c: any) => c._id);
      } else if (conditionData.categoryId) {
        catIds = Array.isArray(conditionData.categoryId) ? conditionData.categoryId : [conditionData.categoryId];
      }
      setSelectedCategories(catIds);
      const vType = conditionData.type;
      setSelectedVehicleType(Array.isArray(vType) ? vType[0] : (vType || ""));
      setErrors({});
    } else if (!conditionData && open) {
      setName("");
      setSelectedCategories([]);
      setSelectedVehicleType("");
      setErrors({});
    }
  }, [conditionData, open]);

  const handleCategoryChange = (event: any) => {
    const value = event.target.value as string[];
    setSelectedCategories(typeof value === 'string' ? (value as string).split(',') : value);
    if (errors.category) setErrors({ ...errors, category: "" });
  };

  const handleVehicleTypeChange = (event: any) => {
    const value = event.target.value as number;
    setSelectedVehicleType(value);
    if (errors.vehicleType) setErrors({ ...errors, vehicleType: "" });
  };

  const validate = () => {
    let tempErrors: any = {};
    let isValid = true;

    if (!name.trim()) {
      tempErrors.name = "Condition Name is required";
      isValid = false;
    }
    if (selectedCategories.length === 0) {
      tempErrors.category = "At least one category is required";
      isValid = false;
    }
    if (!selectedVehicleType) {
      tempErrors.vehicleType = "Vehicle type is required";
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const payload = {
      name,
      categoryId: selectedCategories,
      type: Number(selectedVehicleType),
    };

    try {
      if (conditionData?._id) {
        await updateCondition({ id: conditionData._id, ...payload }).unwrap();
        toast.success("Condition updated successfully");
      } else {
        await addCondition(payload).unwrap();
        toast.success("Condition added successfully");
      }
      setOpen(false);
      onClose();
    } catch (err: any) {
      toast.error(err?.data?.message || "Operation failed");
    }
  };

  return (
    <Modal
      className="modal rejectReason_modal"
      open={open}
      onClose={onClose}
    >
      <div className="modal-dialog">
        <div className="modal-body">
          <div className="modal_title">
            <h2>{conditionData ? "Edit" : "Add"} Condition</h2>
            <button
              type="button"
              className="btn-close"
              onClick={() => { setOpen(false); onClose(); }}
              aria-label="Close"
            >
              <CloseIcon />
            </button>
          </div>

          <div className="form">
            <div className="control_group">
              <label htmlFor="fuel-type-name">Condition Name</label>
              <TextField
                id="fuel-type-name"
                placeholder="Enter Name"
                fullWidth
                hiddenLabel
                value={name}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length <= 40) {
                    setName(value);
                    if (errors.name) setErrors({ ...errors, name: "" });
                  }
                }}
                error={!!errors.name}
                helperText={errors.name}
                inputProps={{ maxLength: 40 }}
              />
            </div>

            <div className="control_group">
              <label htmlFor="Category">Category</label>
              <Select
                multiple
                fullWidth
                hiddenLabel
                value={selectedCategories}
                onChange={handleCategoryChange}
                renderValue={(selected) => {
                  const selectedIds = selected as string[];
                  if (selectedIds.length === 0) return "Select Categories";
                  return selectedIds.map(id =>
                    categoriesData?.data?.data?.find((cat: any) => cat._id === id)?.name
                  ).filter(Boolean).join(", ");
                }}
                error={!!errors.category}
              >
                <MenuItem value={"default"} disabled>Select Categories</MenuItem>
                {categoriesData?.data?.data?.map((cat: any) => (
                  <MenuItem key={cat._id} value={cat._id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
              <div className="control_group">
                <label htmlFor="VehicleType">Vehicle Type</label>
                <Select
                  fullWidth
                  hiddenLabel
                  displayEmpty
                  value={selectedVehicleType}
                  onChange={handleVehicleTypeChange}
                  renderValue={(selected) => {
                    if (!selected) return "Select Vehicle Type";
                    return getVehicleTypeLabel(selected);
                  }}
                  error={!!errors.vehicleType}
                >
                  <MenuItem disabled value="">
                    <em>Select Vehicle Type</em>
                  </MenuItem>
                  {Object.keys(VEHICLE_TYPE)
                    .filter(k => isNaN(Number(k)))
                    .map((key) => (
                      <MenuItem key={key} value={(VEHICLE_TYPE as any)[key]}>
                        {getVehicleTypeLabel((VEHICLE_TYPE as any)[key])}
                      </MenuItem>
                    ))}
                </Select>
                {errors.vehicleType && <div className="error_text" style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{errors.vehicleType}</div>}
              </div>

              {errors.category && <div className="error_text" style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{errors.category}</div>}
            </div>

            <div className="footer_btn_flex">
              <Button
                className="btn btn_primary"
                onClick={handleSave}
                disabled={isAdding || isUpdating}
              >
                {isAdding || isUpdating ? <CircularProgress size={20} color="inherit" /> : "Save"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
